import React, { useEffect, useState } from "react";
import Label from "components/Form/Label";
import TextInput from "components/Form/TextInput";
import InputGroup from "components/Form/InputGroup";
import NextButton from "../NextButton";
import { MailIcon } from "lucide-react";
import ProgressBar from "../ProgressBar";
import { isEmailValid } from "helpers/utils";
import get from "lodash/get";
import { getWebsiteURL } from "helpers/getExternalURLs";

const websiteURL = getWebsiteURL();

const Step = ({
  onChange,
  formData,
  nextStep,
  isEmailUsed,
  onUserSignUpByStep,
  currentStep,
  totalSteps,
  rewardsUserInvitation,
}) => {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState({});
  const [fetchingCode, setFetchingCode] = useState(false);

  const onFormValidation = (_data) => {
    let result = {};
    let hasInvalidInput = false;
    const notAllowedChars = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,<>\/?~0123456789]/;
    const notAllowedOnlyDots = /^\.+$/;
    const firstName = get(_data, "first_name", "");
    const lastName = get(_data, "last_name", "");
    const email = get(_data, "email", "");
    const termsOptIn = get(_data, "terms_opt_in", "");

    if (
      !firstName ||
      notAllowedChars.test(firstName) ||
      notAllowedOnlyDots.test(firstName)
    ) {
      hasInvalidInput = true;
      result.first_name = !firstName
        ? "Please enter a first name."
        : "Invalid entry. Special characters & numbers not allowed.";
    }

    if (
      !lastName ||
      notAllowedChars.test(lastName) ||
      notAllowedOnlyDots.test(lastName)
    ) {
      hasInvalidInput = true;
      result.last_name = !lastName
        ? "Please enter a last name."
        : "Invalid entry. Special characters & numbers not allowed.";
    }

    if (!email || !isEmailValid(email)) {
      hasInvalidInput = true;
      result.email = !email
        ? "Please enter email address."
        : "Invalid email address.";
    }

    if (!termsOptIn) {
      hasInvalidInput = true;
      result.terms_opt_in = true;
    }

    setHasError(hasInvalidInput);
    setErrorMessage(result);
    return hasInvalidInput;
  };

  useEffect(() => {
    if (hasError) onFormValidation(formData);
    if (isEmailUsed) setFetchingCode(false);
  }, [hasError, formData, isEmailUsed]);

  return (
    <div className="mx-auto w-full lg:w-[480px]">
      <div>
        {rewardsUserInvitation?.error && (
          <div
            class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
            role="alert"
          >
            <span class="block sm:inline">Invalid invitation link.</span>
          </div>
        )}
        <h1 className="my-4 text-2xl md:text-3xl font-bold tracking-tight text-primary-700">
          Welcome 👋
        </h1>
        <p className="mt-2 text-sm text-justify text-primary-700">
          Your content already inspires purchases—now it’s time to earn from it.
          With MagicLinks, you can create affiliate links to your favorite
          products, build your own digital storefront, and get cast in campaigns
          with top brands. Apply now to join a community of video-first creators
          turning everyday recommendations into real income.
        </p>
      </div>
      <div className="mt-8">
        <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
        <form action="#" method="POST" className="space-y-4">
          <div>
            <Label>First Name</Label>
            <TextInput
              id="firstname"
              name="firstname"
              type="text"
              autoComplete="firstname"
              placeholder="First Name"
              value={formData?.first_name}
              onChange={(value) =>
                onChange({
                  ...formData,
                  first_name: value.trim(),
                })
              }
              onBlur={() => {
                if (hasError) onFormValidation(formData);
              }}
              hasError={hasError ? get(errorMessage, "first_name", "") : null}
              required
            />
          </div>
          <div>
            <Label>Last Name</Label>
            <TextInput
              id="lastname"
              name="lastname"
              type="text"
              autoComplete="lastname"
              placeholder="Last Name"
              value={formData?.last_name}
              onBlur={() => {
                if (hasError) onFormValidation(formData);
              }}
              hasError={hasError ? get(errorMessage, "last_name", "") : null}
              onChange={(value) =>
                onChange({
                  ...formData,
                  last_name: value.trim(),
                })
              }
              required
            />
          </div>
          <InputGroup
            id="email"
            name="email"
            type="email"
            label={"Email"}
            placeholder="you@example.com"
            value={rewardsUserInvitation?.email || formData?.email}
            disabled={!!rewardsUserInvitation?.email}
            onBlur={() => {
              if (hasError) onFormValidation(formData);
            }}
            hasError={
              hasError ? (
                get(errorMessage, "email", "")
              ) : isEmailUsed ? (
                <>
                  Email already registered. Please log in{" "}
                  <a
                    className="color-brand-500 underline	"
                    href="/rewards_users/login"
                  >
                    here
                  </a>
                </>
              ) : null
            }
            onChange={(value) =>
              onChange({
                ...formData,
                email: value,
              })
            }
            Icon={() => <MailIcon className="h-5 w-5 text-gray-400" />}
          />
          <div className="relative flex items-start mt-4">
            <div className="flex h-5 items-center">
              <input
                id="termsOptIn"
                aria-describedby="terms-description"
                name="terms"
                type="checkbox"
                checked={formData?.terms_opt_in}
                onChange={() => {
                  onChange({
                    ...formData,
                    terms_opt_in: document.getElementById("termsOptIn").checked,
                  });
                }}
                className={`h-4 w-4 form-checkbox rounded ${
                  hasError && errorMessage?.marketing_opt_in
                    ? "text-red-900 border-red-500"
                    : "text-brand-500 focus:ring-brand-500 border-primary-300"
                }`}
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="terms" className="font-medium text-gray-700">
                Read our{" "}
                <a
                  className="text-brand-500 underline font-medium"
                  href={`${websiteURL}terms-of-service`}
                  target="_blank"
                >
                  Terms
                </a>{" "}
                {"&"}{" "}
                <a
                  className="text-brand-500 underline font-medium"
                  href={`${websiteURL}policies`}
                  target="_blank"
                >
                  Policies
                </a>
              </label>
            </div>
          </div>
          <div className="relative flex items-start mt-4">
            <div className="flex h-5 items-center">
              <input
                id="marketingOptIn"
                aria-describedby="newsletter-description"
                name="newsletter"
                type="checkbox"
                checked={formData?.marketing_opt_in}
                onChange={() => {
                  onChange({
                    ...formData,
                    marketing_opt_in:
                      document.getElementById("marketingOptIn").checked,
                  });
                }}
                className={`h-4 w-4 form-checkbox rounded border-primary-300 text-brand-500 focus:ring-brand-500 focus:border-primary-700`}
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="newsletter" className="font-medium text-gray-700">
                I agree to receive communications from MagicLinks
              </label>
              <p id="newsletter-description" className="text-gray-500">
                By checking this box, you consent to allow MagicLinks to store
                and process the personal information submitted to provide the
                content requested.
              </p>
            </div>
          </div>

          <div className="w-full flex justify-end">
            <NextButton
              onClick={() => {
                if (!onFormValidation(formData)) {
                  setFetchingCode(true);
                  onUserSignUpByStep(nextStep);
                }
              }}
              disabled={
                fetchingCode ||
                !formData?.terms_opt_in ||
                !formData?.marketing_opt_in
              }
              loading={fetchingCode}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default Step;
